/**
 * WebXR session setup and the honest readout of what the browser granted.
 *
 * Every field here is optional in the WebXR IDL and absent on browsers that do
 * not implement depth sensing, so the checks are not defensive padding — they
 * are the difference between "depth is off" and "depth does not exist".
 */

export const SESSION_INIT: XRSessionInit = {
  requiredFeatures: ['dom-overlay'],
  optionalFeatures: ['depth-sensing', 'hit-test'],
  depthSensing: {
    // CPU-first: the geometry is built from the depth values on the CPU, and
    // the GPU path exposes only a texture. Chrome-Android grants nothing else
    // anyway — `gpu-optimized` and `float32` both end in NOTREACHED() in
    // `xr_depth_manager.cc`.
    usagePreference: ['cpu-optimized', 'gpu-optimized'],
    dataFormatPreference: ['luminance-alpha', 'float32'],
    // `XRDepthType` landed in the spec after the rest of depth sensing shipped.
    // What WebXR has handed us so far is ARCore's *smoothed* and fully
    // inpainted depth, and that smoothing is the whole of the excess error at
    // close range. Unknown dictionary members are ignored, so on a browser
    // without the binding this is a no-op; `smooth` is listed second so such a
    // browser is never left unsatisfiable.
    depthTypeRequest: ['raw', 'smooth'],
  },
}

export type SupportState = { kind: 'ready' } | { kind: 'blocked'; reason: string }

export async function probeSupport(): Promise<SupportState> {
  if (!window.isSecureContext) {
    return {
      kind: 'blocked',
      reason: 'Needs HTTPS (or localhost). Open the LAN URL over https — see docs/RUNNING.md.',
    }
  }
  if (!navigator.xr) {
    return {
      kind: 'blocked',
      reason: 'No navigator.xr — this needs Chrome on Android with ARCore installed.',
    }
  }
  if (!(await navigator.xr.isSessionSupported('immersive-ar'))) {
    return {
      kind: 'blocked',
      reason: 'immersive-ar is not supported on this device or browser.',
    }
  }
  return { kind: 'ready' }
}

export interface DepthGrant {
  granted: boolean
  /** `cpu-optimized`, `gpu-optimized`, or `none` when depth was refused. */
  usage: string
  /**
   * `raw` or `smooth` when the browser implements `XRDepthType`, `—` when it
   * has no such attribute at all. Those are different answers: the second means
   * the request was never understood, not that `smooth` was chosen.
   */
  type: string
  /** Human-readable explanation, present only when `granted` is false. */
  reason: string | null
}

/**
 * What the session actually granted for depth.
 *
 * `enabledFeatures` is the only honest source: a refused optional feature is
 * otherwise invisible until the depth API returns nothing mid-frame, which
 * reads on screen exactly like a device that has not bootstrapped yet.
 */
export function describeDepthGrant(session: XRSession): DepthGrant {
  const enabled = session.enabledFeatures ?? []
  const granted = enabled.includes('depth-sensing')
  const usage = session.depthUsage ?? 'none'
  const type = session.depthType ?? '—'

  if (!granted) {
    return {
      granted: false,
      usage,
      type,
      reason:
        enabled.length === 0
          ? 'This browser does not report enabledFeatures, so depth sensing could not be confirmed.'
          : 'Depth sensing was requested but refused. ARCore may be missing or out of date.',
    }
  }

  if (usage === 'gpu-optimized') {
    return {
      granted: false,
      usage,
      type,
      reason: 'Depth was granted as gpu-optimized; this demo reads the CPU buffer.',
    }
  }

  return { granted: true, usage, type, reason: null }
}

/** `hit-test` carries the analytic floor, so losing it changes what the demo shows. */
export function hasHitTest(session: XRSession): boolean {
  return (session.enabledFeatures ?? []).includes('hit-test')
}
